import type { AudioAssetRef, Project } from '@/domain/types';
import type { WaveformPeaks } from '@/assets/waveformTypes';
import { WAVEFORM_VERSION } from '@/assets/waveformTypes';
import { migrateAndParse } from '@/domain/format/migrate';
import { toSerializable } from '@/domain/format/serialize';
import {
  StorageError,
  type ProjectSummary,
  type RecoverySnapshot,
  type RenderSummary,
  type StorageProvider,
  type StorageUsage,
  type StoredAsset,
  type StoredRender,
} from './StorageProvider';
import { openDatabase, requestToPromise, toStorageError, transactionDone } from './idb';

// The app was called Resonate when this database was first created, and the
// name stays: renaming a database does not move what is in it, so a rename
// would open every browser that has saved a project to an empty list.
const DB_NAME = 'resonate-daw';
const DB_VERSION = 2;

const STORE_PROJECTS = 'projects';
const STORE_ASSETS = 'assets';
const STORE_WAVEFORMS = 'waveforms';
const STORE_RECOVERY = 'recovery';
const STORE_SETTINGS = 'settings';
const STORE_RENDERS = 'renders';
const STORE_RENDER_META = 'renderMeta';

const RECOVERY_KEY = 'current';

/**
 * How much space cached renders may take, in total.
 *
 * The arithmetic is worth stating, because it is not small: three minutes of
 * stereo audio at 48kHz is 8.6 million frames, which is 69MB of 32-bit samples.
 * Half a gigabyte therefore holds a handful of long renders or a great many
 * short ones — and loops, which are what people actually stretch and transpose
 * repeatedly, are short.
 *
 * A budget that can be exceeded by one entry is not a budget, so a single render
 * larger than a quarter of it is not stored at all. It still works; it is simply
 * recomputed each session, which is what happened to every render before this
 * cache existed.
 */
export const RENDER_BUDGET_BYTES = 512 * 1024 * 1024;

interface ProjectRecord {
  id: string;
  name: string;
  createdAt: number;
  modifiedAt: number;
  trackCount: number;
  clipCount: number;
  sizeBytes: number;
  /** The project document, stored as JSON text so it is opaque to IDB indexing. */
  document: string;
}

interface AssetRecord {
  id: string;
  meta: AudioAssetRef;
  blob: Blob;
}

interface RecoveryRecord {
  key: string;
  projectId: string;
  projectName: string;
  savedAt: number;
  superseded: boolean;
  document: string;
}

/**
 * A render, and its metadata, in two stores rather than one.
 *
 * Deciding what to evict means reading every entry's size and last use. If those
 * lived on the same record as the samples, that decision would have to page
 * hundreds of megabytes of audio through memory to answer a question about a few
 * hundred bytes of bookkeeping.
 */
interface RenderRecord {
  id: string;
  /** The full content key, kept so a hash collision reads as a miss. */
  key: string;
  sampleRate: number;
  channels: Float32Array[];
}

interface RenderMetaRecord {
  id: string;
  key: string;
  assetId: string;
  bytes: number;
  lastUsedAt: number;
  /**
   * A counter, incremented on every write and every read.
   *
   * `lastUsedAt` is a wall-clock time and is what the storage panel shows, but it
   * cannot order eviction: several renders are routinely prepared inside one
   * millisecond, and ordering by a value they all share makes the choice
   * arbitrary — so the entry just written can be the one evicted. A sequence has
   * no resolution to run out of.
   */
  usedSeq: number;
}

/**
 * A short, stable id for a render key.
 *
 * A warp render's key describes every span of its bend, so it can run to
 * kilobytes. That is a poor primary key — it is indexed, compared and held in
 * memory for every entry — so keys are hashed, and the full key is kept on the
 * record. Two keys that collided would make the second overwrite the first, and
 * a read of the first would then find a record whose key does not match and
 * report a miss. A miss is always safe: it recomputes.
 */
function renderId(key: string): string {
  let a = 0x811c9dc5;
  let b = 0x01000193;
  for (let i = 0; i < key.length; i++) {
    const c = key.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193);
    b = Math.imul(b + c, 0x85ebca6b) ^ (b >>> 13);
  }
  const hex = (n: number): string => (n >>> 0).toString(16).padStart(8, '0');
  return `${hex(a)}${hex(b)}${key.length.toString(16)}`;
}

function renderBytes(render: StoredRender): number {
  return render.channels.reduce((total, channel) => total + channel.byteLength, 0);
}

/**
 * The local-first storage implementation: everything lives in IndexedDB on the
 * user's machine. Nothing in this class talks to a network.
 *
 * Projects are stored as JSON text rather than as structured objects. That
 * costs a serialise step but buys three things that matter more: a stable
 * on-disk shape independent of IDB's structured-clone rules, a cheap size
 * measurement for the project browser, and byte-identical behaviour between
 * "save to IndexedDB" and "export a .dawproject file".
 *
 * Audio lives separately, as Blobs keyed by asset id, and is shared between
 * projects. Deleting a project therefore does not delete audio; `collectGarbage`
 * does, once nothing references it.
 */
export class IndexedDBStorage implements StorageProvider {
  readonly id = 'indexeddb';
  private db: IDBDatabase | null = null;
  private opening: Promise<IDBDatabase> | null = null;
  private readonly renderBudgetBytes: number;
  /** Null until the first render is touched, then one past the highest seen. */
  private renderSeq: number | null = null;

  /**
   * The render budget is injectable so that eviction can be tested against a
   * budget of a few kilobytes rather than by allocating half a gigabyte.
   */
  constructor(options: { renderBudgetBytes?: number } = {}) {
    this.renderBudgetBytes = Math.max(0, options.renderBudgetBytes ?? RENDER_BUDGET_BYTES);
  }

  async init(): Promise<void> {
    await this.database();
  }

  private async database(): Promise<IDBDatabase> {
    if (this.db) return this.db;
    if (!this.opening) {
      this.opening = openDatabase({
        name: DB_NAME,
        version: DB_VERSION,
        upgrade: (db, oldVersion) => {
          if (oldVersion < 1) {
            const projects = db.createObjectStore(STORE_PROJECTS, { keyPath: 'id' });
            projects.createIndex('modifiedAt', 'modifiedAt');
            db.createObjectStore(STORE_ASSETS, { keyPath: 'id' });
            db.createObjectStore(STORE_WAVEFORMS, { keyPath: 'assetId' });
            db.createObjectStore(STORE_RECOVERY, { keyPath: 'key' });
            db.createObjectStore(STORE_SETTINGS, { keyPath: 'key' });
          }
          if (oldVersion < 2) {
            // Renders are derived data: an upgrade that added the stores empty
            // is complete, because anything missing is simply recomputed.
            db.createObjectStore(STORE_RENDERS, { keyPath: 'id' });
            db.createObjectStore(STORE_RENDER_META, { keyPath: 'id' });
          }
        },
      }).then((db) => {
        this.db = db;
        return db;
      });
    }
    try {
      return await this.opening;
    } catch (error) {
      this.opening = null;
      throw error;
    }
  }

  private async run<T>(
    stores: string[],
    mode: IDBTransactionMode,
    action: string,
    body: (tx: IDBTransaction) => Promise<T> | T,
  ): Promise<T> {
    const db = await this.database();
    try {
      const tx = db.transaction(stores, mode);
      const result = await body(tx);
      await transactionDone(tx);
      return result;
    } catch (error) {
      throw toStorageError(error, action);
    }
  }

  // -- projects ------------------------------------------------------------

  async listProjects(): Promise<ProjectSummary[]> {
    const records = await this.run([STORE_PROJECTS], 'readonly', 'list projects', (tx) =>
      requestToPromise(tx.objectStore(STORE_PROJECTS).getAll() as IDBRequest<ProjectRecord[]>),
    );
    return records
      .map((r) => ({
        id: r.id,
        name: r.name,
        createdAt: r.createdAt,
        modifiedAt: r.modifiedAt,
        sizeBytes: r.sizeBytes,
        trackCount: r.trackCount,
        clipCount: r.clipCount,
      }))
      .sort((a, b) => b.modifiedAt - a.modifiedAt);
  }

  async loadProject(projectId: string): Promise<Project | null> {
    const record = await this.run([STORE_PROJECTS], 'readonly', 'load the project', (tx) =>
      requestToPromise(tx.objectStore(STORE_PROJECTS).get(projectId) as IDBRequest<ProjectRecord | undefined>),
    );
    if (!record) return null;
    return parseStoredDocument(record.document, projectId);
  }

  async saveProject(project: Project): Promise<void> {
    const record = toProjectRecord(project);
    await this.run([STORE_PROJECTS], 'readwrite', 'save the project', (tx) => {
      tx.objectStore(STORE_PROJECTS).put(record);
    });
  }

  async deleteProject(projectId: string): Promise<void> {
    await this.run([STORE_PROJECTS], 'readwrite', 'delete the project', (tx) => {
      tx.objectStore(STORE_PROJECTS).delete(projectId);
    });
  }

  // -- assets --------------------------------------------------------------

  async putAsset(meta: AudioAssetRef, blob: Blob): Promise<void> {
    const record: AssetRecord = { id: meta.id, meta, blob };
    await this.run([STORE_ASSETS], 'readwrite', 'store the audio file', (tx) => {
      tx.objectStore(STORE_ASSETS).put(record);
    });
  }

  async getAsset(assetId: string): Promise<StoredAsset | null> {
    const record = await this.run([STORE_ASSETS], 'readonly', 'read the audio file', (tx) =>
      requestToPromise(tx.objectStore(STORE_ASSETS).get(assetId) as IDBRequest<AssetRecord | undefined>),
    );
    return record ? { meta: record.meta, blob: record.blob } : null;
  }

  async hasAsset(assetId: string): Promise<boolean> {
    const key = await this.run([STORE_ASSETS], 'readonly', 'check the audio file', (tx) =>
      requestToPromise(tx.objectStore(STORE_ASSETS).getKey(assetId)),
    );
    return key !== undefined;
  }

  async deleteAsset(assetId: string): Promise<void> {
    await this.run([STORE_ASSETS, STORE_WAVEFORMS], 'readwrite', 'delete the audio file', (tx) => {
      tx.objectStore(STORE_ASSETS).delete(assetId);
      tx.objectStore(STORE_WAVEFORMS).delete(assetId);
    });
  }

  async listAssetIds(): Promise<string[]> {
    const keys = await this.run([STORE_ASSETS], 'readonly', 'list audio files', (tx) =>
      requestToPromise(tx.objectStore(STORE_ASSETS).getAllKeys()),
    );
    return keys.map(String);
  }

  // -- waveforms -----------------------------------------------------------

  async putWaveform(assetId: string, peaks: WaveformPeaks): Promise<void> {
    await this.run([STORE_WAVEFORMS], 'readwrite', 'cache the waveform', (tx) => {
      tx.objectStore(STORE_WAVEFORMS).put({ ...peaks, assetId });
    });
  }

  async getWaveform(assetId: string): Promise<WaveformPeaks | null> {
    const record = await this.run([STORE_WAVEFORMS], 'readonly', 'read the cached waveform', (tx) =>
      requestToPromise(tx.objectStore(STORE_WAVEFORMS).get(assetId) as IDBRequest<WaveformPeaks | undefined>),
    );
    if (!record || record.version !== WAVEFORM_VERSION) return null;
    return record;
  }

  // -- renders -------------------------------------------------------------

  async putRender(key: string, assetId: string, render: StoredRender): Promise<void> {
    const bytes = renderBytes(render);
    if (bytes === 0 || bytes > this.renderBudgetBytes / 4) return;

    const id = renderId(key);
    const record: RenderRecord = { id, key, sampleRate: render.sampleRate, channels: render.channels };
    const meta: RenderMetaRecord = {
      id,
      key,
      assetId,
      bytes,
      lastUsedAt: Date.now(),
      usedSeq: await this.nextRenderSeq(),
    };
    await this.run([STORE_RENDERS, STORE_RENDER_META], 'readwrite', 'cache the render', (tx) => {
      tx.objectStore(STORE_RENDERS).put(record);
      tx.objectStore(STORE_RENDER_META).put(meta);
    });
    // In a transaction of its own: eviction is housekeeping, and holding the
    // write open across a scan of every entry would make the common case — a
    // cache that is nowhere near full — pay for the rare one.
    await this.enforceRenderBudget();
  }

  async getRender(key: string): Promise<StoredRender | null> {
    const id = renderId(key);
    const record = await this.run([STORE_RENDERS], 'readonly', 'read a cached render', (tx) =>
      requestToPromise(tx.objectStore(STORE_RENDERS).get(id) as IDBRequest<RenderRecord | undefined>),
    );
    // A record under this id whose key is different is a hash collision, and is
    // not this render. Reporting a miss recomputes, which is always correct.
    if (!record || record.key !== key) return null;

    await this.touchRender(id);
    return { channels: record.channels, sampleRate: record.sampleRate };
  }

  async listRenders(): Promise<RenderSummary[]> {
    const metas = await this.readRenderMeta();
    return metas.map((m) => ({ key: m.key, assetId: m.assetId, bytes: m.bytes, lastUsedAt: m.lastUsedAt }));
  }

  async deleteRender(key: string): Promise<void> {
    await this.dropRenders([renderId(key)]);
  }

  private async readRenderMeta(): Promise<RenderMetaRecord[]> {
    return this.run([STORE_RENDER_META], 'readonly', 'list cached renders', (tx) =>
      requestToPromise(tx.objectStore(STORE_RENDER_META).getAll() as IDBRequest<RenderMetaRecord[]>),
    );
  }

  private async dropRenders(ids: readonly string[]): Promise<void> {
    if (ids.length === 0) return;
    await this.run([STORE_RENDERS, STORE_RENDER_META], 'readwrite', 'drop cached renders', (tx) => {
      for (const id of ids) {
        tx.objectStore(STORE_RENDERS).delete(id);
        tx.objectStore(STORE_RENDER_META).delete(id);
      }
    });
  }

  /** Records that a render was wanted, which is what makes eviction least-recently-used. */
  private async touchRender(id: string): Promise<void> {
    const usedSeq = await this.nextRenderSeq();
    await this.run([STORE_RENDER_META], 'readwrite', 'note a render was used', async (tx) => {
      const store = tx.objectStore(STORE_RENDER_META);
      const meta = await requestToPromise(store.get(id) as IDBRequest<RenderMetaRecord | undefined>);
      if (meta) store.put({ ...meta, lastUsedAt: Date.now(), usedSeq });
    });
  }

  /**
   * The next use counter, continuing from where the last session left off.
   *
   * Read from the store once, on first use, so that a render kept from a
   * previous session is not treated as older or newer than it is. Everything
   * after that is arithmetic.
   */
  private async nextRenderSeq(): Promise<number> {
    if (this.renderSeq === null) {
      const metas = await this.readRenderMeta();
      this.renderSeq = metas.reduce((highest, m) => Math.max(highest, m.usedSeq ?? 0), 0);
    }
    return ++this.renderSeq;
  }

  /**
   * Drops least-recently-used renders until the cache is inside its budget.
   *
   * The render just written is never the one dropped, and needs no special case
   * to say so: it has the highest use counter, so it is last in this order, and
   * the per-entry cap means evicting everything ahead of it always frees enough.
   * A guard here would be a defence against something that cannot happen, which
   * reads as though it can.
   */
  private async enforceRenderBudget(): Promise<void> {
    const metas = await this.readRenderMeta();
    let total = metas.reduce((sum, m) => sum + m.bytes, 0);
    if (total <= this.renderBudgetBytes) return;

    const doomed: string[] = [];
    for (const meta of [...metas].sort((a, b) => (a.usedSeq ?? 0) - (b.usedSeq ?? 0))) {
      if (total <= this.renderBudgetBytes) break;
      doomed.push(meta.id);
      total -= meta.bytes;
    }
    await this.dropRenders(doomed);
  }

  // -- recovery ------------------------------------------------------------

  async saveRecovery(snapshot: RecoverySnapshot): Promise<void> {
    const record: RecoveryRecord = {
      key: RECOVERY_KEY,
      projectId: snapshot.projectId,
      projectName: snapshot.projectName,
      savedAt: snapshot.savedAt,
      superseded: snapshot.superseded,
      document: JSON.stringify(toSerializable(snapshot.project)),
    };
    await this.run([STORE_RECOVERY], 'readwrite', 'write recovery data', (tx) => {
      tx.objectStore(STORE_RECOVERY).put(record);
    });
  }

  async loadRecovery(): Promise<RecoverySnapshot | null> {
    const record = await this.run([STORE_RECOVERY], 'readonly', 'read recovery data', (tx) =>
      requestToPromise(tx.objectStore(STORE_RECOVERY).get(RECOVERY_KEY) as IDBRequest<RecoveryRecord | undefined>),
    );
    if (!record) return null;
    try {
      return {
        projectId: record.projectId,
        projectName: record.projectName,
        savedAt: record.savedAt,
        superseded: record.superseded,
        project: parseStoredDocument(record.document, record.projectId),
      };
    } catch {
      // A corrupt recovery blob must never block startup.
      await this.clearRecovery();
      return null;
    }
  }

  async clearRecovery(): Promise<void> {
    await this.run([STORE_RECOVERY], 'readwrite', 'clear recovery data', (tx) => {
      tx.objectStore(STORE_RECOVERY).delete(RECOVERY_KEY);
    });
  }

  // -- settings ------------------------------------------------------------

  async getSetting<T>(key: string): Promise<T | null> {
    const record = await this.run([STORE_SETTINGS], 'readonly', 'read a setting', (tx) =>
      requestToPromise(tx.objectStore(STORE_SETTINGS).get(key) as IDBRequest<{ key: string; value: T } | undefined>),
    );
    return record ? record.value : null;
  }

  async setSetting<T>(key: string, value: T): Promise<void> {
    await this.run([STORE_SETTINGS], 'readwrite', 'write a setting', (tx) => {
      tx.objectStore(STORE_SETTINGS).put({ key, value });
    });
  }

  // -- housekeeping --------------------------------------------------------

  async collectGarbage(): Promise<{ removedAssets: number; removedRenders: number; freedBytes: number }> {
    const [projects, assetIds, recovery] = await Promise.all([
      this.run([STORE_PROJECTS], 'readonly', 'scan projects', (tx) =>
        requestToPromise(tx.objectStore(STORE_PROJECTS).getAll() as IDBRequest<ProjectRecord[]>),
      ),
      this.listAssetIds(),
      this.loadRecovery(),
    ]);

    const referenced = new Set<string>();
    const collect = (doc: string): void => {
      // A substring scan is used rather than a full parse: this runs over every
      // stored project and only needs to know which ids appear.
      for (const id of assetIds) if (doc.includes(id)) referenced.add(id);
    };
    for (const record of projects) collect(record.document);
    if (recovery) collect(JSON.stringify(recovery.project.assets));

    let removedAssets = 0;
    let freedBytes = 0;
    for (const id of assetIds) {
      if (referenced.has(id)) continue;
      const stored = await this.getAsset(id);
      freedBytes += stored?.blob.size ?? 0;
      await this.deleteAsset(id);
      removedAssets++;
    }

    // A render of audio that no longer exists can never be wanted again, and it
    // is far larger than the encoded audio it came from.
    const renders = await this.readRenderMeta();
    const orphans = renders.filter((r) => !referenced.has(r.assetId));
    for (const orphan of orphans) freedBytes += orphan.bytes;
    await this.dropRenders(orphans.map((r) => r.id));

    return { removedAssets, removedRenders: orphans.length, freedBytes };
  }

  async estimateUsage(): Promise<StorageUsage> {
    // Renders are counted from our own bookkeeping rather than from the browser's
    // estimate, which reports one number for everything. A cache that quietly
    // takes half a gigabyte should be a number the musician can look at.
    const renderBytes = await this.readRenderMeta()
      .then((metas) => metas.reduce((sum, m) => sum + m.bytes, 0))
      .catch(() => 0);
    const base = { renderBytes, renderBudgetBytes: this.renderBudgetBytes };

    const nav = (globalThis as { navigator?: Navigator }).navigator;
    const storage = nav?.storage;
    if (!storage?.estimate) return { usageBytes: null, quotaBytes: null, persisted: false, ...base };
    try {
      const estimate = await storage.estimate();
      const persisted = storage.persisted ? await storage.persisted() : false;
      return {
        usageBytes: estimate.usage ?? null,
        quotaBytes: estimate.quota ?? null,
        persisted,
        ...base,
      };
    } catch {
      return { usageBytes: null, quotaBytes: null, persisted: false, ...base };
    }
  }

  /**
   * Asks the browser to exempt our data from eviction under storage pressure.
   * Chromium grants this silently for installed/engaged sites; a refusal is not
   * an error, just a weaker guarantee we surface in the storage panel.
   */
  async requestPersistence(): Promise<boolean> {
    const storage = (globalThis as { navigator?: Navigator }).navigator?.storage;
    if (!storage?.persist) return false;
    try {
      return await storage.persist();
    } catch {
      return false;
    }
  }

  close(): void {
    this.db?.close();
    this.db = null;
    this.opening = null;
  }
}

function toProjectRecord(project: Project): ProjectRecord {
  const stamped = toSerializable(project);
  const document = JSON.stringify(stamped);
  return {
    id: stamped.meta.id,
    name: stamped.meta.name,
    createdAt: stamped.meta.createdAt,
    modifiedAt: stamped.meta.modifiedAt,
    trackCount: stamped.trackOrder.length,
    clipCount: Object.keys(stamped.clips).length,
    sizeBytes: document.length,
    document,
  };
}

function parseStoredDocument(document: string, projectId: string): Project {
  let raw: unknown;
  try {
    raw = JSON.parse(document);
  } catch (error) {
    throw new StorageError(
      'corrupt',
      `Project "${projectId}" could not be read: its stored data is not valid JSON. ` +
        'If you have an exported .dawproject file, import that instead.',
      error,
    );
  }
  return migrateAndParse(raw).project;
}
